import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  MessageSquare, 
  Image, 
  Star, 
  LogOut,
  Menu,
  X,
  Users,
  Settings,
  FileText
} from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';
import { useState } from 'react';

// Admin panel translations (Spanish/English)
const adminTranslations = {
  es: {
    dashboard: 'Panel',
    orders: 'Pedidos',
    services: 'Servicios',
    portfolio: 'Portafolio',
    testimonials: 'Testimonios',
    messages: 'Mensajes',
    users: 'Usuarios',
    invoices: 'Facturas',
    settings: 'Configuración',
    logout: 'Cerrar Sesión'
  },
  en: {
    dashboard: 'Dashboard',
    orders: 'Orders',
    services: 'Services',
    portfolio: 'Portfolio',
    testimonials: 'Testimonials',
    messages: 'Messages',
    users: 'Users',
    invoices: 'Invoices',
    settings: 'Settings',
    logout: 'Logout'
  }
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const { logout } = useAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [lang, setLang] = useState('es'); // Default to Spanish
  const t = adminTranslations[lang];

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const menuItems = [
    { name: t.dashboard, path: '/admin/dashboard', icon: LayoutDashboard },
    { name: t.orders, path: '/admin/orders', icon: ShoppingCart },
    { name: t.services, path: '/admin/services', icon: Package },
    { name: t.portfolio, path: '/admin/portfolio', icon: Image },
    { name: t.testimonials, path: '/admin/testimonials', icon: Star },
    { name: t.messages, path: '/admin/messages', icon: MessageSquare },
    { name: t.invoices, path: '/admin/invoices', icon: FileText },
    { name: t.users, path: '/admin/users', icon: Users },
    { name: t.settings, path: '/admin/settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0c0f] via-[#141619] to-[#1a1d23] circuit-pattern">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-gradient-to-b from-[#1a1d23] to-[#141619] border-r border-[#2a3441] z-50 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#2a3441]">
            <div className="flex items-center justify-between">
              {sidebarOpen && (
                <h1 className="text-xl font-bold text-gold-metallic">PrimeDigital</h1>
              )}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-[#2a3441] rounded-lg transition-colors"
              >
                {sidebarOpen ? <X className="w-5 h-5 text-gray-400" /> : <Menu className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
          </div>

          {/* Language Toggle (only when sidebar is open) */}
          {sidebarOpen && (
            <div className="px-4 py-3 border-b border-[#2a3441]">
              <div className="flex space-x-2">
                <button
                  onClick={() => setLang('es')}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    lang === 'es' 
                      ? 'bg-[#D4AF37] text-[#141619]' 
                      : 'bg-[#2a3441] text-gray-400 hover:text-white'
                  }`}
                >
                  🇪🇸 Español
                </button>
                <button
                  onClick={() => setLang('en')}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    lang === 'en' 
                      ? 'bg-[#D4AF37] text-[#141619]' 
                      : 'bg-[#2a3441] text-gray-400 hover:text-white'
                  }`}
                >
                  🇬🇧 English
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] font-semibold'
                        : 'text-gray-400 hover:bg-[#2a3441] hover:text-white'
                    }`
                  }
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span>{item.name}</span>}
                </NavLink>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-[#2a3441]">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 w-full px-4 py-3 text-gray-400 hover:bg-red-500/10 hover:text-red-500 rounded-lg transition-all duration-300"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{t.logout}</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
